/**
 * useSpeechRecognition — browser-native voice-to-text hook.
 *
 * Wraps the `webkitSpeechRecognition` API (Chrome / Edge / Safari /
 * Android Chrome). Returns:
 *   • start()       — request mic permission + begin listening
 *   • stop()        — finalise transcript and stop listening
 *   • listening     — bool, true while the mic is active
 *   • transcript    — live concatenated transcript (finalised + interim)
 *   • supported     — bool, false on Firefox (no SpeechRecognition)
 *   • error         — string|null, last error message
 *
 * Design choices
 *   • `continuous: true` so we capture multi-sentence questions without
 *     the engine auto-pausing on a 2-second silence.
 *   • `interimResults: true` so the live transcript updates as the user
 *     speaks (Google-style waveform feel).
 *   • Auto-restart on `aborted` events — Chromium's recogniser can drop
 *     mid-sentence on background tabs; we transparently re-start so the
 *     user doesn't notice unless they explicitly tap stop.
 *   • Locale defaults to `en-US` but accepts `lang` so we can match the
 *     learning path's language for multilingual users later.
 */
import { useCallback, useEffect, useRef, useState } from "react";

function _getSpeechRecognitionClass() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export default function useSpeechRecognition({ lang = "en-US" } = {}) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState(null);

  const recRef = useRef(null);
  const finalisedRef = useRef("");          // committed (final) transcript
  // While `listening` is true and the engine auto-stops, we restart.
  // When the user taps stop, we set this to false so the auto-restart
  // doesn't keep the mic open against the user's intent.
  const userWantsListenRef = useRef(false);

  const supported = _getSpeechRecognitionClass() !== null;

  // Stable callback the recogniser instance attaches its handlers to.
  const _attach = useCallback((rec) => {
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (ev) => {
      let interim = "";
      // Walk the new results since lastIndex. Final results get
      // committed into finalisedRef so a mid-utterance auto-restart
      // doesn't lose what we've already heard.
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        const text = r[0]?.transcript || "";
        if (r.isFinal) {
          finalisedRef.current = (finalisedRef.current + " " + text).trim();
        } else {
          interim += text;
        }
      }
      setTranscript((finalisedRef.current + " " + interim).trim());
    };

    rec.onerror = (ev) => {
      const code = ev?.error || "unknown";
      // `no-speech` and `aborted` are non-fatal — they happen when the
      // user pauses or another script (e.g. our own TTS) momentarily
      // captures the mic. Don't show those to the user.
      if (code !== "no-speech" && code !== "aborted") {
        setError(
          code === "not-allowed"
            ? "Microphone permission denied. Please enable it in your browser."
            : code === "audio-capture"
              ? "No microphone detected on this device."
              : `Voice recognition error: ${code}`,
        );
      }
    };

    rec.onend = () => {
      // Auto-restart if the user hasn't explicitly stopped — Chromium
      // ends the session after ~60s or when the tab loses focus.
      if (userWantsListenRef.current) {
        try { rec.start(); return; } catch { /* fall through */ }
      }
      setListening(false);
    };
  }, [lang]);

  const start = useCallback(() => {
    if (!supported) return;
    setError(null);
    finalisedRef.current = "";
    setTranscript("");
    const Cls = _getSpeechRecognitionClass();
    const rec = new Cls();
    recRef.current = rec;
    _attach(rec);
    userWantsListenRef.current = true;
    try {
      rec.start();
      setListening(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[useSpeechRecognition] start() threw", e);
      setError("Couldn't start the microphone. Please try again.");
      userWantsListenRef.current = false;
    }
  }, [supported, _attach]);

  const stop = useCallback(() => {
    userWantsListenRef.current = false;
    if (recRef.current) {
      try { recRef.current.stop(); } catch { /* ignore */ }
    }
    setListening(false);
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      userWantsListenRef.current = false;
      try { recRef.current?.abort?.(); } catch { /* ignore */ }
    };
  }, []);

  return { start, stop, listening, transcript, supported, error };
}
